import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, LogOut, Loader2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// Apple 5.1.2(i): appearing as "nearby" is an explicit, per-visit action.
export default function NearbyCheckInBar({ coords, source = "manual", city }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/nearby/checkin").then(({ data }) => setStatus(data)).catch(() => setStatus({ checked_in: false }));
  }, []);

  const checkIn = async () => {
    if (!coords) return;
    setBusy(true);
    try {
      const { data } = await api.post("/nearby/checkin", { lat: coords.lat, lng: coords.lng, city: city || undefined, source });
      setStatus({ checked_in: true, checked_in_until: data.checked_in_until, hours: data.hours });
      setOpen(false);
      toast.success(`Checked in — you're visible nearby for ${data.hours}h.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not check in.");
    } finally {
      setBusy(false);
    }
  };
  const checkOut = async () => {
    setBusy(true);
    try {
      await api.delete("/nearby/checkin");
      setStatus({ checked_in: false });
      toast("You're hidden from the map again.");
    } finally {
      setBusy(false);
    }
  };

  if (!status) return null;
  const until = status.checked_in_until ? new Date(status.checked_in_until) : null;

  return (
    <div className="absolute left-3 right-3 z-[500] pointer-events-none" style={{ top: "calc(env(safe-area-inset-top) + 4.25rem)" }} data-testid="nearby-checkin-bar">
      <div className="pointer-events-auto max-w-md mx-auto bg-zinc-950/90 backdrop-blur-md border border-white/10 px-3 py-2 flex items-center gap-3">
        <MapPin size={16} className={status.checked_in ? "text-emerald-400" : "text-zinc-400"} />
        <div className="flex-1 min-w-0 text-xs">
          {status.checked_in ? (
            <span className="text-zinc-200" data-testid="nearby-checkin-status">Checked in · visible nearby until {until?.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
          ) : (
            <span className="text-zinc-400" data-testid="nearby-checkin-status">Browsing only — your <span className="text-white">exact</span> pin is hidden until you check in. Recently active members appear as approximate city-level pins.</span>
          )}
        </div>
        {status.checked_in ? (
          <Button size="sm" variant="outline" disabled={busy} onClick={checkOut} data-testid="nearby-checkout-btn" className="rounded-full h-8 border-white/15 text-zinc-200 text-xs"><LogOut size={12} className="mr-1" /> Check out</Button>
        ) : (
          <Button size="sm" disabled={busy || !coords} onClick={() => setOpen(true)} data-testid="nearby-checkin-btn" className="rounded-full h-8 bg-rose-600 hover:bg-rose-500 text-white text-xs">Check in</Button>
        )}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-950 border-white/10 text-white rounded-none max-w-sm" data-testid="nearby-checkin-confirm">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 grid place-items-center bg-rose-600/15 border border-rose-500/40 text-rose-300"><ShieldAlert size={18} /></span>
              <DialogTitle className="font-display text-2xl">Show me as nearby for 24 hours?</DialogTitle>
            </div>
            <DialogDescription className="text-xs text-zinc-400 pt-2 leading-relaxed">
              Others will see a jittered pin (±0.15 mi) and a rounded distance — never your exact address. You can check out anytime. Meeting someone? Meet in public, tell a friend, and trust your instincts.{" "}
              <Link to="/legal/privacy-policy" className="underline text-rose-300" data-testid="nearby-checkin-privacy-link">Privacy Policy</Link>
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-2">
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)} data-testid="nearby-checkin-cancel-btn" className="rounded-full h-9 text-zinc-300 text-xs">Not now</Button>
            <Button size="sm" disabled={busy} onClick={checkIn} data-testid="nearby-checkin-confirm-btn" className="rounded-full h-9 bg-rose-600 hover:bg-rose-500 text-white text-xs">{busy ? <Loader2 size={12} className="animate-spin" /> : "Check in now"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
